import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Paper, Stack, Divider,
  IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { PROYECTO_COLOR } from "../theme";
import ArchivosProyectoSection from "../components/ArchivosProyectoSection";
import AgregarFacturaDialog from "../components/AgregarFacturaDialog";
import BuscarYAgregarDialog from "../components/BuscarYAgregarDialog";
import EditarMontoParcialDialog from "../components/EditarMontoParcialDialog";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.74rem",
  padding: "3px 6px",
  color: theme.palette.mode === "dark" ? theme.palette.common.white : PROYECTO_COLOR,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : `${PROYECTO_COLOR}14`,
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 36 },
  "& .MuiInputBase-input": { py: "8px", fontSize: "0.82rem" },
  "& .MuiInputLabel-root": { fontSize: "0.82rem" },
};

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const bs = (n) => `${Number(n || 0).toFixed(2)} Bs.`;

const sumar = (lista, campo) => lista.reduce((acc, r) => acc + Number(r[campo] || 0), 0);

const TotalCellSx = { fontWeight: 800, borderTop: "2px solid", borderTopColor: "divider" };

const Seccion = ({ titulo, onAgregar, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: PROYECTO_COLOR }}>{titulo}</Typography>
      {onAgregar && (
        <Button size="small" startIcon={<AddIcon />} onClick={onAgregar} sx={{ color: PROYECTO_COLOR }}>
          Agregar
        </Button>
      )}
    </Box>
    {children}
  </Paper>
);

const ProyectoDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    nombre: "", descripcion: "", fecha: "",
    monto_contrato: "", potencia_instalada: "", gestion: "", respaldo: "", responsable_tecnico: "",
  });
  const [saving, setSaving] = useState(false);
  const [errorForm, setErrorForm] = useState("");

  const [dialogo, setDialogo] = useState(null); // 'factura' | 'egreso' | 'compra' | 'ot' | null
  const [editarMontoParcial, setEditarMontoParcial] = useState(null); // {tipo, item}

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(`/api/proyectos/${id}`)
      .then((res) => {
        const data = res.data?.data;
        setProyecto(data);
        setForm({
          nombre: data.nombre, descripcion: data.descripcion, fecha: (data.fecha || "").slice(0, 10),
          monto_contrato: data.monto_contrato ?? "", potencia_instalada: data.potencia_instalada || "",
          gestion: data.gestion || "", respaldo: data.respaldo || "", responsable_tecnico: data.responsable_tecnico || "",
        });
      })
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar el proyecto."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleGuardarDatos = async () => {
    setSaving(true);
    setErrorForm("");
    try {
      await axios.put(`/api/proyectos/${id}`, form);
      cargar();
    } catch (e) {
      setErrorForm(e?.response?.data?.message || "Error al guardar los datos.");
    } finally {
      setSaving(false);
    }
  };

  const quitarVinculo = async (tipo, cod) => {
    if (!window.confirm("¿Quitar este vínculo del proyecto?")) return;
    await axios.delete(`/api/proyectos/${id}/vinculo/${tipo}/${cod}`);
    cargar();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      </DashboardLayout>
    );
  }

  if (error || !proyecto) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}><Alert severity="error">{error || "Proyecto no encontrado."}</Alert></Box>
      </DashboardLayout>
    );
  }

  const { resumen } = proyecto;

  return (
    <DashboardLayout>
      <PageHeader
        title={`Proyecto #${proyecto.id}`}
        subtitle={proyecto.cliente.razon_social}
        showBack onBack={() => navigate("/proyectos")}
      />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {errorForm && <Alert severity="error" sx={{ mb: 1.5 }}>{errorForm}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.5, mb: 1.5 }}>
            <TextField
              size="small" label="Nombre" value={form.nombre}
              onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="Descripción" value={form.descripcion}
              onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
              sx={compactFieldSx}
            />
            <TextField
              size="small" type="date" label="Fecha" InputLabelProps={{ shrink: true }}
              value={form.fecha}
              onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
              sx={compactFieldSx}
            />
          </Box>
          <TextField
            size="small" label="Cliente" value={`${proyecto.cliente.razon_social} / ${proyecto.cliente.servicio_descripcion}`}
            disabled fullWidth sx={{ ...compactFieldSx, mb: 1.5 }}
          />

          <Divider textAlign="left" sx={{ fontSize: "0.75rem", mb: 1.5 }}>Datos del reporte</Divider>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr 1fr" }, gap: 1.5, mb: 1.5 }}>
            <TextField
              size="small" type="number" label="Monto Contrato" value={form.monto_contrato}
              onChange={(e) => setForm((p) => ({ ...p, monto_contrato: e.target.value }))}
              inputProps={{ min: 0, step: "0.01" }}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="Potencia Instalada W" value={form.potencia_instalada}
              onChange={(e) => setForm((p) => ({ ...p, potencia_instalada: e.target.value }))}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="Gestión" value={form.gestion}
              onChange={(e) => setForm((p) => ({ ...p, gestion: e.target.value }))}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="Respaldo" value={form.respaldo}
              onChange={(e) => setForm((p) => ({ ...p, respaldo: e.target.value }))}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="Responsable Técnico" value={form.responsable_tecnico}
              onChange={(e) => setForm((p) => ({ ...p, responsable_tecnico: e.target.value }))}
              sx={compactFieldSx}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={handleGuardarDatos} disabled={saving}
              sx={{ bgcolor: PROYECTO_COLOR, "&:hover": { bgcolor: "#4527a0" } }}>
              {saving ? "Guardando..." : "Actualizar Datos"}
            </Button>
          </Box>
        </Paper>

        <ArchivosProyectoSection proyectoId={id} />

        <Seccion titulo="Facturas" onAgregar={() => setDialogo("factura")}>
          {resumen.facturas.length === 0 ? (
            <Alert severity="info">Sin facturas vinculadas.</Alert>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.3, px: 0.75, fontSize: "0.76rem" } }}>
                <TableHead>
                  <TableRow>
                    <Th>N° Factura</Th><Th>Fecha</Th><Th>Tipo</Th>
                    <Th align="right">Bs.</Th><Th align="right">$us.</Th><Th align="right">Saldo</Th>
                    <Th align="center">Quitar</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumen.facturas.map((f) => (
                    <TableRow key={f.cod} hover>
                      <TableCell>{f.num_fac || "-"}</TableCell>
                      <TableCell>{formatFecha(f.fecha_fac)}</TableCell>
                      <TableCell>{f.pre_fac === "si" ? "Pre-Factura" : "Factura"}</TableCell>
                      <TableCell align="right">{Number(f.monto_total).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(f.dolares).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(f.saldo).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Quitar">
                          <IconButton size="small" color="error" onClick={() => quitarVinculo("cod_factura", f.cod)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} sx={TotalCellSx}>Total</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturas, "monto_total").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturas, "dolares").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturas, "saldo").toFixed(2)}</TableCell>
                    <TableCell sx={TotalCellSx} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Seccion>

        <Seccion titulo="Comprobante de Egreso" onAgregar={() => setDialogo("egreso")}>
          {resumen.egresos.length === 0 ? (
            <Alert severity="info">Sin egresos vinculados.</Alert>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.3, px: 0.75, fontSize: "0.76rem" } }}>
                <TableHead>
                  <TableRow>
                    <Th>Cod</Th><Th>N° Cheque</Th><Th>Fecha</Th><Th>Dirigido a</Th>
                    <Th>Concepto</Th><Th>Servicio</Th><Th align="right">Bs.</Th><Th align="right">$us.</Th>
                    <Th align="right">Monto Parcial</Th><Th align="center">Editar</Th><Th align="center">Quitar</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumen.egresos.map((eg) => (
                    <TableRow key={eg.id} hover>
                      <TableCell>{eg.id}</TableCell>
                      <TableCell>{eg.nocheque}</TableCell>
                      <TableCell>{formatFecha(eg.fecha)}</TableCell>
                      <TableCell>{eg.nombre}</TableCell>
                      <TableCell>{eg.concepto}</TableCell>
                      <TableCell>{eg.servicio}</TableCell>
                      <TableCell align="right">{Number(eg.bolivianos).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(eg.dolares).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(eg.monto_parcial).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar monto parcial">
                          <IconButton size="small" sx={{ color: PROYECTO_COLOR }}
                            onClick={() => setEditarMontoParcial({ tipo: "cod_egreso", item: eg })}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Quitar">
                          <IconButton size="small" color="error" onClick={() => quitarVinculo("cod_egreso", eg.id)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={6} sx={TotalCellSx}>Total</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.egresos, "bolivianos").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.egresos, "dolares").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.egresos, "monto_parcial").toFixed(2)}</TableCell>
                    <TableCell colSpan={2} sx={TotalCellSx} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Seccion>

        {resumen.facturasEgreso.length > 0 && (
          <Seccion titulo="Facturas cargadas en egresos (crédito fiscal)">
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.3, px: 0.75, fontSize: "0.76rem" } }}>
                <TableHead>
                  <TableRow>
                    <Th>Id</Th><Th>N° Factura</Th><Th>Denominación</Th>
                    <Th align="right">Importe Total</Th><Th align="right">Importe No Sujeto</Th>
                    <Th align="right">Subtotal</Th><Th align="right">Descuento</Th><Th align="right">Crédito Fiscal</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumen.facturasEgreso.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>{f.id}</TableCell>
                      <TableCell>{f.nro_factura}</TableCell>
                      <TableCell>{f.denominacion}</TableCell>
                      <TableCell align="right">{f.importe_total.toFixed(2)}</TableCell>
                      <TableCell align="right">{f.importe_no_sujeto.toFixed(2)}</TableCell>
                      <TableCell align="right">{f.subtotal.toFixed(2)}</TableCell>
                      <TableCell align="right">{f.descuento.toFixed(2)}</TableCell>
                      <TableCell align="right">{f.credito_fiscal.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} sx={TotalCellSx}>Total</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturasEgreso, "importe_total").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturasEgreso, "importe_no_sujeto").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturasEgreso, "subtotal").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturasEgreso, "descuento").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.facturasEgreso, "credito_fiscal").toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Seccion>
        )}

        <Seccion titulo="Orden de Compra" onAgregar={() => setDialogo("compra")}>
          {resumen.compras.length === 0 ? (
            <Alert severity="info">Sin órdenes de compra vinculadas.</Alert>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.3, px: 0.75, fontSize: "0.76rem" } }}>
                <TableHead>
                  <TableRow>
                    <Th>Cod</Th><Th>Fecha</Th><Th>Proveedor</Th><Th>Moneda</Th>
                    <Th align="right">Bs.</Th><Th align="right">$us.</Th>
                    <Th align="right">Monto Parcial</Th><Th align="center">Editar</Th><Th align="center">Quitar</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumen.compras.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.id}</TableCell>
                      <TableCell>{formatFecha(c.fecha)}</TableCell>
                      <TableCell>{c.proveedor}</TableCell>
                      <TableCell>{c.moneda_label}</TableCell>
                      <TableCell align="right">{Number(c.monto_bolivianos).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(c.monto_dolares).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(c.monto_parcial).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar monto parcial">
                          <IconButton size="small" sx={{ color: PROYECTO_COLOR }}
                            onClick={() => setEditarMontoParcial({ tipo: "cod_compra", item: c })}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Quitar">
                          <IconButton size="small" color="error" onClick={() => quitarVinculo("cod_compra", c.id)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} sx={TotalCellSx}>Total</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.compras, "monto_bolivianos").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.compras, "monto_dolares").toFixed(2)}</TableCell>
                    <TableCell align="right" sx={TotalCellSx}>{sumar(resumen.compras, "monto_parcial").toFixed(2)}</TableCell>
                    <TableCell colSpan={2} sx={TotalCellSx} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Seccion>

        <Seccion titulo="Órdenes de Trabajo (OT)" onAgregar={() => setDialogo("ot")}>
          {proyecto.ots.length === 0 ? (
            <Alert severity="info">Sin OTs vinculadas.</Alert>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.3, px: 0.75, fontSize: "0.76rem" } }}>
                <TableHead>
                  <TableRow>
                    <Th>Cod OT</Th><Th>Razón Social</Th><Th>Descripción</Th>
                    <Th>Servicio</Th><Th>Sucursal Facturar</Th><Th align="center">Quitar</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proyecto.ots.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>{o.id}</TableCell>
                      <TableCell>{o.razon_social}</TableCell>
                      <TableCell>{o.descripcion}</TableCell>
                      <TableCell>{o.nombre_servicio}</TableCell>
                      <TableCell>{o.sucursal_facturar}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Quitar">
                          <IconButton size="small" color="error" onClick={() => quitarVinculo("cod_ot", o.id)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Seccion>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: PROYECTO_COLOR }}>
            Resumen
          </Typography>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Monto total Factura</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{bs(resumen.montoFacturaBs)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Monto Neto Factura (-16%)</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{bs(resumen.montoImpuestoBs)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Comprobante de egreso</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>-{bs(resumen.montoEgresoBs)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Orden de Compra</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>-{bs(resumen.montoCompraBs)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Facturas de egresos (crédito fiscal)</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{bs(resumen.montoCreditoFiscalBs)}</Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Saldo Final</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: resumen.saldoFinalBs < 0 ? "error.main" : PROYECTO_COLOR }}>
                {bs(resumen.saldoFinalBs)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <AgregarFacturaDialog
        open={dialogo === "factura"}
        onClose={() => setDialogo(null)}
        onAgregar={async (cods) => { await axios.post(`/api/proyectos/${id}/facturas`, { cods }); cargar(); }}
      />
      <BuscarYAgregarDialog
        open={dialogo === "egreso"}
        onClose={() => setDialogo(null)}
        titulo="Agregar Comprobante de Egreso"
        buscarEndpoint="/api/proyectos/buscar-egresos"
        columns={[
          { key: "id", label: "Cod" },
          { key: "nocheque", label: "N° Cheque" },
          { key: "fecha", label: "Fecha", format: formatFecha },
          { key: "nombre", label: "Dirigido a" },
          { key: "concepto", label: "Concepto" },
          { key: "bolivianos", label: "Bs.", format: (v) => Number(v).toFixed(2) },
        ]}
        onAgregar={async (cods) => { await axios.post(`/api/proyectos/${id}/egresos`, { cods }); cargar(); }}
      />
      <BuscarYAgregarDialog
        open={dialogo === "compra"}
        onClose={() => setDialogo(null)}
        titulo="Agregar Orden de Compra"
        buscarEndpoint="/api/proyectos/buscar-compras"
        columns={[
          { key: "id", label: "Cod" },
          { key: "fecha", label: "Fecha", format: formatFecha },
          { key: "proveedor", label: "Proveedor" },
          { key: "estado", label: "Estado" },
        ]}
        onAgregar={async (cods) => { await axios.post(`/api/proyectos/${id}/compras`, { cods }); cargar(); }}
      />
      <BuscarYAgregarDialog
        open={dialogo === "ot"}
        onClose={() => setDialogo(null)}
        titulo="Agregar Orden de Trabajo"
        buscarEndpoint="/api/proyectos/buscar-ots"
        columns={[
          { key: "id", label: "Cod OT" },
          { key: "razon_social", label: "Razón Social" },
          { key: "descripcion", label: "Descripción" },
          { key: "nombre_servicio", label: "Servicio" },
        ]}
        onAgregar={async (cods) => { await axios.post(`/api/proyectos/${id}/ots`, { cods }); cargar(); }}
      />
      <EditarMontoParcialDialog
        open={Boolean(editarMontoParcial)}
        proyectoId={id}
        tipo={editarMontoParcial?.tipo}
        item={editarMontoParcial?.item}
        onClose={() => setEditarMontoParcial(null)}
        onGuardado={() => { setEditarMontoParcial(null); cargar(); }}
      />
    </DashboardLayout>
  );
};

export default ProyectoDetallePage;
